import React, { useState, forwardRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  Box,
  IconButton,
  Slide,
  Chip,
  TextField,
  CircularProgress,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { theme } from '../../../../theme';
import { ScreeningStatus, AssignmentStatus, StatusChip } from '../CandidateContent/CandidateContent.styles';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { statusColorMap } from '../../JobManagement/CreateJobPosting/CreateJob/ReadJob';
import { toTitleCase } from '../CandidateContent/CandidateContent';
import { removeSpacing } from '../../../Common/Sanitize/sanitizeReactQuill';

// Slide transition component
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

// Updated interface
interface NotesPopupProps {
  open: boolean;
  onClose: () => void;
  userStatus: string; // Accepting userStatus as a prop
  candidateNotes: { MapID: number, NoteValue: string; RecruiterName: string }[];
  handleSave: (candidateNum: number | null, noteValue: string) => void
  candidateMapId: number | null;
  candidateName: string | null;
  notesLoading: boolean
}



const NotesPopup: React.FC<NotesPopupProps> = ({ open, onClose, candidateMapId, userStatus, candidateNotes, handleSave, candidateName, notesLoading }) => {
  const [newNote, setNewNote] = useState<string>('');
  const [notes, setNotes] = useState<{ MapID: number, NoteValue: string; RecruiterName: string }[]>([]); // List of notes with status


  const handleNoteChange = (value: string) => {
    setNewNote(value); // Update new note text
  };

  useEffect(() => {
    setNotes(candidateNotes || [])
  }, [candidateNotes])

  useEffect(() => {
    setNewNote('')
  }, [open])

  const statusStyles = statusColorMap[userStatus] || {
    color: "#1565C0",
    bgColor: "#E3F2FD",
    borderColor: "#BBDEFB",
  };




  const handleSaveNote = () => {
    const note = removeSpacing(newNote)
    handleSave(candidateMapId, note)
    setNewNote('')
  }

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
      PaperProps={{
        sx: {
          maxWidth: '22vw', // Responsive width
          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          p: '2vh 1.5vw 0 1vw',
          gap: '1vw',
          position: 'relative',
        }}
      >
        {/* Left: Icon */}
        <CreateOutlinedIcon
          sx={{
            color: theme.palette.primary.main,
            bgcolor: theme.palette.secondary.light,
            fontSize: '2.5vw',
            border: '0.07vw solid #EEEEEE',
            borderRadius: '0.8vw',
            mt: '0.2vw',
          }}
        />

        {/* Center: Title and Subtitle */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6">Notes</Typography>
          <Typography variant="caption" sx={{ marginBottom: '0.8vh' }}>
            Notes during the interview process
          </Typography>
        </Box>

        {/* Cross Icon at Top Right */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: '1.2vh',
            right: '1vw',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <Box sx={{ display: 'flex', gap: '0.7vw', flexDirection: "column", mt: 1,ml: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {toTitleCase(candidateName || "")}
        </Typography>
        <StatusChip
          bg={statusStyles.bgColor}
          border={statusStyles.borderColor}
          color={statusStyles.color}
        >
          ● {userStatus}
        </StatusChip>
      </Box>

      <Divider sx={{ mt: 2 }} />

      {/* Content Area */}
      <DialogContent sx={{ overflowY: 'auto', pt: 1 }}>
        {notesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '20vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          notes.map((note, index) => (
            <Box my={2} key={index}>
              <Typography variant="subtitle1" display="flex" alignItems="center">
                {note.RecruiterName}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflow: 'hidden',
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: note.NoteValue }} />
              </Typography>
              <Divider sx={{ my: 2.5 }} />
            </Box>
          ))
        )}
      </DialogContent>



      {/* Add Note Section */}
      <DialogActions sx={{ padding: '1vh 1vw', gap: 4, display: 'flex', flexDirection: 'column' }}>
        <ReactQuill
          value={newNote}
          onChange={handleNoteChange}
          style={{ height: '20vh', width: '100%' }}
          modules={{
            toolbar: [
              ['bold', 'italic', 'link'],
              [{ list: 'bullet' }, { list: 'ordered' }],
            ],
            clipboard: {
              matchVisual: false, // Prevents Quill from auto-converting visual line breaks to `<p><br></p>`
            },
          }}
        />
        <Button
          onClick={handleSaveNote}
          variant="contained"
          disabled={
            !newNote || newNote.trim() === '' || newNote.trim() === '<p><br></p>'
          }
          sx={{
            width: '100%',
            height: '2vw',
            minWidth: '2vw',
            minHeight: '2vw',
            marginTop: '2vh',
            gap: 1,
          }}
        >
          <i className="fa-solid fa-plus"></i>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesPopup;
